import { FC, useEffect } from 'react'
import {
  Create,
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useTranslate,
  useGetList,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { FormSection, PageHeader, formSurfaceSx, pageSx } from '../../components/ui'
import { CertificatesCreateContact } from './forms/Contact.form'
import { Alert, Box } from '@mui/material'
import { TargetSystem, MunicipalityOut, CertificateType, InstanceOut } from '../../types/apiRequests'
import { INSTANCES_PER_PAGE, ZakenForm } from './forms/zaken/Zaken.form'
import PrefillContactFromMunicipality from './forms/zaken/PrefillContactFromMunicipality'
import { getCapabilities, targetSystemChoices, typeChoices } from './targetSystems'
import { hostnameValidation } from '../../utils/validation'

const SetDefaultMunicipality: FC<{ municipalityId: string }> = ({ municipalityId }) => {
  const { setValue, getValues } = useFormContext()

  useEffect(() => {
    const timeout = setTimeout(() => {
      const currentValue = getValues('municipalityId') as string | undefined
      if (!currentValue) {
        setValue('municipalityId', municipalityId, {
          shouldValidate: false,
          shouldDirty: false,
        })
      }
    }, 100)

    return () => clearTimeout(timeout)
  }, [municipalityId, setValue, getValues])

  return null
}

// Target system select. A switch clears metaData and a type the new system doesn't
// allow, so e.g. a Zaken instance/host never ends up on a Waarderen certificate.
const TargetSystemSelect: FC<{ choices: typeof targetSystemChoices }> = ({ choices }) => {
  const { setValue, getValues } = useFormContext()

  const onChange = (): void => {
    const allowedTypes = getCapabilities(getValues('targetSystem') as TargetSystem)?.allowedTypes ?? []
    setValue('metaData', {})
    if (allowedTypes.length === 1) {
      setValue('type', allowedTypes[0])
    } else if (!allowedTypes.includes(getValues('type') as CertificateType)) {
      setValue('type', null)
    }
  }

  return (
    <SelectInput
      source="targetSystem"
      label="resources.certificates.fields.targetSystem"
      validate={[required()]}
      choices={choices}
      onChange={onChange}
      fullWidth
    />
  )
}

// Type select. A switch clears the koppeling, as its choices differ per type: a stufconfig
// koppeling must not stay on a PKI certificate. The interface select then drops an interface
// of another module itself.
const TypeSelect: FC<{ allowedTypes: CertificateType[] }> = ({ allowedTypes }) => {
  const { setValue } = useFormContext()

  const onChange = (): void => {
    // undefined, so the PKI form's defaultValue applies again.
    setValue('metaData.samlKoppeling', undefined)
  }

  return (
    <SelectInput
      source="type"
      label="resources.certificates.fields.type"
      validate={[required()]}
      choices={typeChoices.filter(choice => allowedTypes.includes(choice.id))}
      onChange={onChange}
      fullWidth
    />
  )
}

const CertificatesCreate: FC = () => {
  const translate = useTranslate()

  const { data: municipalities } = useGetList<MunicipalityOut>('municipality')
  const hasAtLeastOneMunicipality = !!municipalities && municipalities.length > 0

  // Zaken only for an organization linked to Zaken: without a link the API returns no instances.
  const { data: instances, isPending: instancesPending } = useGetList<InstanceOut>('instances', {
    pagination: { page: 1, perPage: INSTANCES_PER_PAGE },
  })
  const hasZaken = !!instances?.length
  const choices = hasZaken ? targetSystemChoices : targetSystemChoices.filter(c => c.id !== TargetSystem.ZAKEN)

  // defaultValues only apply on the form's first render: wait for the instances.
  if (instancesPending) return null

  return (
    <Create component="div" actions={false} redirect="show" sx={pageSx}>
      <PageHeader
        crumbs={[{ label: translate('resources.certificates.list'), to: '/certificate' }]}
        title={translate('resources.certificates.create')}
      />
      {/* Surface wraps the form so sections and toolbar share one rounded card. */}
      <Box sx={formSurfaceSx}>
        <SimpleForm defaultValues={{ targetSystem: hasZaken ? TargetSystem.ZAKEN : undefined }}>
          {hasAtLeastOneMunicipality ? (
            <>
              <SetDefaultMunicipality municipalityId={municipalities[0].id} />

              <FormSection
                title={translate('resources.certificates.sections.targetSystem')}
                description={translate('resources.certificates.sections.targetSystemHelp')}
              >
                <TargetSystemSelect choices={choices} />

                <ReferenceInput source="municipalityId" reference="municipality">
                  <SelectInput
                    optionText="organisatienNaam"
                    label="resources.certificates.fields.municipality"
                    optionValue="id"
                    validate={[required()]}
                    fullWidth
                  />
                </ReferenceInput>
              </FormSection>

              <FormSection
                title={translate('resources.certificates.sections.contact')}
                description={translate('resources.certificates.sections.contactHelp')}
              >
                <PrefillContactFromMunicipality />
                <CertificatesCreateContact />
              </FormSection>

              <FormSection
                title={translate('resources.certificates.sections.certificate')}
                description={translate('resources.certificates.sections.certificateHelp')}
              >
                <FormDataConsumer>
                  {({ formData }) => {
                    const capabilities = getCapabilities(formData?.targetSystem as TargetSystem)
                    return (
                      <>
                        {/* Only the types the target system supports. */}
                        <TypeSelect allowedTypes={capabilities?.allowedTypes ?? []} />
                        {/* Systems without own hostname logic (e.g. Bezwaren): user enters it. */}
                        {capabilities?.commonName === 'manual' && (
                          <TextInput
                            source="metaData.commonName"
                            label="resources.certificates.fields.host"
                            validate={[required(), ...hostnameValidation]}
                            fullWidth
                          />
                        )}
                        {formData?.targetSystem === TargetSystem.ZAKEN && <ZakenForm />}
                      </>
                    )
                  }}
                </FormDataConsumer>
              </FormSection>
            </>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {translate('resources.certificates.alerts.noMunicipalityConfigured')}
            </Alert>
          )}
        </SimpleForm>
      </Box>
    </Create>
  )
}

export default CertificatesCreate
